import { useState } from "react";
import { Navigate } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { ROLES, can } from "@family/shared";
import type {
  CreateInviteRequest,
  Invite,
  InvitesListResponse,
  Role,
} from "@family/shared";

export function InvitesPage() {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<Role>("contributor");
  const [expiresInHours, setExpiresInHours] = useState<number>(168);
  const [latest, setLatest] = useState<Invite | null>(null);
  const [copied, setCopied] = useState(false);

  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const list = useQuery<InvitesListResponse>({
    queryKey: ["invites", slug],
    queryFn: () => apiFetch<InvitesListResponse>(`/families/${slug}/invites`),
    enabled: Boolean(slug),
  });

  const create = useMutation<Invite, Error, CreateInviteRequest>({
    mutationFn: (body) =>
      apiFetch<Invite>(`/families/${slug}/invites`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (inv) => {
      setLatest(inv);
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: ["invites", slug] });
    },
  });

  async function copyLink() {
    if (!latest) return;
    try {
      await navigator.clipboard.writeText(latest.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // no-op
    }
  }

  if (meQuery.isLoading) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  if (!primary || !can(primary.role, "members:invite")) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <h1>Invites — {primary.familyName}</h1>

        <section className="stack">
          <h2>Create an invite link</h2>
          <div className="card stack">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "var(--s-3)",
              }}
            >
              <div>
                <label htmlFor="invite-role">Role</label>
                <select
                  id="invite-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as Role)}
                  disabled={create.isPending}
                  className="select-skapa"
                >
                  {ROLES.filter((r) => r !== "site_admin").map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="invite-expiry">Valid for (hours)</label>
                <input
                  id="invite-expiry"
                  type="number"
                  min={1}
                  max={720}
                  value={expiresInHours}
                  onChange={(e) =>
                    setExpiresInHours(parseInt(e.target.value, 10) || 168)
                  }
                  disabled={create.isPending}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => create.mutate({ role, expiresInHours })}
              disabled={create.isPending}
            >
              {create.isPending ? "Creating…" : "Create invite link"}
            </button>
            {create.error && (
              <p className="text-error">{create.error.message}</p>
            )}

            {latest && (
              <div
                style={{
                  background: "var(--c-bg-alt)",
                  padding: "var(--s-3)",
                  borderRadius: 8,
                  display: "flex",
                  gap: "var(--s-2)",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <code
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    background: "transparent",
                    padding: 0,
                  }}
                >
                  {latest.url}
                </code>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={copyLink}
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="stack">
          <h2>All invites</h2>
          {list.isLoading && <p className="text-muted">Loading…</p>}
          {list.data && list.data.invites.length === 0 && (
            <p className="text-muted">No invites created yet.</p>
          )}
          {list.data && list.data.invites.length > 0 && (
            <ul className="list-unstyled">
              {list.data.invites.map((inv) => {
                const expired = new Date(inv.expiresAt).getTime() < Date.now();
                const status = inv.consumedAt
                  ? "used"
                  : expired
                    ? "expired"
                    : "pending";
                return (
                  <li key={inv.id} className="card">
                    <div className="row-between">
                      <div>
                        <span className="badge">{inv.role}</span>{" "}
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.9rem" }}
                        >
                          expires {new Date(inv.expiresAt).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })}
                        </span>
                      </div>
                      <span
                        className={`badge ${
                          status === "used" ? "badge--admin" : ""
                        }`}
                      >
                        {status}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </>
  );
}
